/**
 * ============================================================================
 * THEMECONTEXT.JS - Dark Mode Support für HomeM
 * ============================================================================
 * 
 * Features:
 * ✅ Light/Dark Mode Toggle
 * ✅ localStorage Persistenz
 * ✅ v1.1: Light Mode als Default (ignoriert System-Präferenz)
 * ✅ v1.1: Warnung nur 1x (nicht bei jedem Render)
 * ✅ Smooth Transitions
 * 
 * @version 1.1.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Storage Key
const STORAGE_KEY = 'homem_theme';

// ✅ v1.1: Singleton-Flag für Warnung (nur 1x loggen)
let hasWarnedAboutMissingProvider = false;

// Theme Context
const ThemeContext = createContext(null);

// ============================================================================
// THEME PROVIDER
// ============================================================================
export function ThemeProvider({ children }) {
  // ✅ v1.1 FIX: Light Mode als Default - KEINE System-Präferenz!
  const [isDark, setIsDark] = useState(() => {
    // 1. Check localStorage
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) {
      return saved === 'dark';
    }
    
    // 2. ✅ FIX: Direkt Light Mode zurückgeben, KEINE System-Präferenz!
    return false;
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');
    
    // Optional: Update document class for global CSS
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  // Listen for system theme changes (nur wenn User explizit 'system' wählt)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set preference
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === null) {
        setIsDark(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Toggle function
  const toggleTheme = useCallback(() => {
    setIsDark(prev => !prev);
  }, []);

  // Set specific theme
  const setTheme = useCallback((theme) => {
    if (theme === 'dark') {
      setIsDark(true);
    } else if (theme === 'light') {
      setIsDark(false);
    } else if (theme === 'system') {
      // Reset to system preference
      localStorage.removeItem(STORAGE_KEY);
      if (window.matchMedia) {
        setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
      }
    }
  }, []);

  // Context Value
  const value = {
    isDark,
    isLight: !isDark,
    theme: isDark ? 'dark' : 'light',
    toggleTheme,
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (!context) {
    // ✅ v1.1 FIX: Warnung nur EINMAL anzeigen (nicht bei jedem Render!)
    if (!hasWarnedAboutMissingProvider) {
      hasWarnedAboutMissingProvider = true;
      console.warn('useTheme must be used within a ThemeProvider. Using default light theme.');
    }
    
    // Fallback wenn Provider fehlt
    return {
      isDark: false,
      isLight: true,
      theme: 'light',
      toggleTheme: () => {},
      setTheme: () => {}
    };
  }
  
  return context;
}

// ============================================================================
// THEME TOGGLE COMPONENT
// ============================================================================
export function ThemeToggle({ style = {} }) {
  const { isDark, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        border: isDark ? '2px solid #4B5563' : '2px solid #FED7AA',
        background: isDark ? '#374151' : '#FFF7ED',
        cursor: 'pointer',
        fontSize: '1.2rem',
        transition: 'all 0.3s ease',
        ...style
      }}
      title={isDark ? 'Light Mode aktivieren' : 'Dark Mode aktivieren'}
    >
      {isDark ? '☀️' : '🌙'}
    </button>
  );
}

// ============================================================================
// THEME COLORS HELPER
// ============================================================================
export function getThemeColors(isDark) {
  if (isDark) {
    return {
      // Backgrounds
      bg: '#1F2937',
      bgSecondary: '#111827',
      bgTertiary: '#374151',
      bgCard: '#1F2937',
      
      // Text
      text: '#F9FAFB',
      textSecondary: '#D1D5DB',
      textMuted: '#9CA3AF',
      
      // Borders
      border: '#4B5563',
      borderLight: '#374151',
      
      // Accent (Orange)
      accent: '#F97316',
      accentLight: '#FB923C',
      accentDark: '#EA580C',
      accentBg: 'rgba(249, 115, 22, 0.15)',
      
      // Status
      success: '#10B981',
      successBg: 'rgba(16, 185, 129, 0.15)',
      warning: '#F59E0B',
      warningBg: 'rgba(245, 158, 11, 0.15)',
      error: '#EF4444',
      errorBg: 'rgba(239, 68, 68, 0.15)',
      info: '#3B82F6',
      infoBg: 'rgba(59, 130, 246, 0.15)',
      
      // Shadows
      shadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      shadowLight: '0 2px 8px rgba(0, 0, 0, 0.2)'
    };
  }
  
  return {
    // Backgrounds
    bg: '#FFFFFF',
    bgSecondary: '#FFF7ED',
    bgTertiary: '#FAFAFA',
    bgCard: '#FFFFFF',
    
    // Text
    text: '#1F2937',
    textSecondary: '#374151',
    textMuted: '#6B7280',
    
    // Borders
    border: '#FED7AA',
    borderLight: '#FFEDD5',
    
    // Accent (Orange)
    accent: '#F97316',
    accentLight: '#F59E0B',
    accentDark: '#EA580C',
    accentBg: '#FFF7ED',
    
    // Status
    success: '#10B981',
    successBg: '#D1FAE5',
    warning: '#F59E0B',
    warningBg: '#FEF3C7',
    error: '#EF4444',
    errorBg: '#FEF2F2',
    info: '#3B82F6',
    infoBg: '#DBEAFE',
    
    // Shadows
    shadow: '0 4px 20px rgba(249, 115, 22, 0.1)',
    shadowLight: '0 2px 8px rgba(0, 0, 0, 0.05)'
  };
}

export default ThemeContext;